import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router'
import { Box, Button, Heading, Text, Input, Textarea, Badge, SimpleTabs, SimpleTable, SimplePopover } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils'

const JOBS = [
  'email_stats',
  'send_stage_reminders',
  'send_scheduled_automatic_messages',
  'send_meeting_reminders_to_personnel',
  'send_participant_event_reminders',
  'check_participants_behind_schedule',
  'daily_trial_update_email',
  'weekly_trial_update_email',
  'sms_status_update',
  'remove_invalid_push_tokens',
  'check_pending_transcode_jobs',
  'sync_dexcom',
  'refresh_garmin_tokens',
  'bill_whereby_sessions',
  'bill_bird_messages',
  'bill_kycaid_verifications',
  'generate_trial_insights',
  'compute_trial_stats'
]

export default function Root () {
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamCode, setNewTeamCode] = useState('')
  const [teams, setTeams] = useState([])
  const [users, setUsers] = useState([])
  const [teamQuery, setTeamQuery] = useState('')
  const [contacts, setContacts] = useState([])
  const [selectedContact, setSelectedContact] = useState('')
  const [contactQuery, setContactQuery] = useState('')
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const navigate = useNavigate()
  const { user, updateUser } = useStore()

  useEffect(() => {
    if (!user) return
    api.root.getTeams(({ teams }) => setTeams(teams))
    api.root.getUsers(({ users }) => setUsers(users))
  }, [user])

  useEffect(() => {
    if (selectedContact && user) {
      api.root.getMessages(selectedContact, ({ messages }) => setMessages(messages))
    }
  }, [user, selectedContact])

  useEffect(() => {
    if (!user) return
    fetchContacts(contactQuery)
  }, [contactQuery, user])

  const fetchContacts = useCallback(utils.debounce((contactQuery) => {
    api.root.getContacts(contactQuery, ({ contacts }) => {
      setContacts(contacts)
    })
  }, 300), [])

  function sendMessage () {
    api.root.sendMessage(selectedContact, { body: newMessage }, () => {
      setNewMessage('')
      api.root.getMessages(selectedContact, ({ messages }) => setMessages(messages))
    }, err => utils.toast('error', 'Unable to send message', err.message))
  }

  function createTeam () {
    api.teams.create({ code: newTeamCode, name: newTeamName }, newTeam => {
      const newTeams = Object.assign([], teams)
      newTeams.push(newTeam)
      setTeams(newTeams)
      navigate(`/root/teams/${newTeam._id}/edit`)
    }, err => utils.toast('error', 'Unable to create team', err.message))
  }
  function switchTeam (teamId) {
    api.users.updateTeam(user._id, teamId, () => {
      updateUser(user._id, { team: teamId })
    }, err => utils.toast('error', 'Unable to switch team', err.message))
  }

  function testSentry () {
    const x = {}
    console.log(x.y.z)
  }

  function runJob (job) {
    api.root.runScheduledJob(job, () => {
      utils.toast('info', 'Job has been run')
    }, err => utils.toast('error', 'Unable to run job', err.message))
  }

  const filteredTeams = teamQuery ? teams.filter(t => t.name.toLowerCase().includes(teamQuery.toLowerCase())) : teams

  return (
    <Box>
      <SimpleTabs
        tabs={[
          {
            title: 'Teams',
            content: (
              <Box>
                <Box display='flex' alignItems='center' mb={4} justifyContent='space-between'>
                  <Box display='flex' alignItems='center'>
                    <Heading as='h3' size='md'>Existing teams</Heading>
                    <Input size='sm' w='150px' ml={2} placeholder='Search teams' value={teamQuery} onChange={e => setTeamQuery(e.target.value)} autoFocus />
                  </Box>
                  <SimplePopover
                    trigger={<Button colorPalette='primary'>Create team</Button>}
                    header='Create new team'
                    body={
                      <>
                        <Input mt={5} placeholder='Team code' value={newTeamCode} onChange={e => setNewTeamCode(e.target.value)} />
                        <Input mt={3} placeholder='Team name' value={newTeamName} onChange={e => setNewTeamName(e.target.value)} />
                        <Button mt={3} colorPalette='primary' onClick={createTeam}>Create team</Button>
                      </>
                  }
                  />
                </Box>
                <SimpleTable
                  tableProps={{ size: 'sm' }}
                  headers={['Team name', 'Actions']}
                  rows={filteredTeams.map(t => [
                    t.name,
                    <Box key={t._id}>
                      {user.team !== t._id
                        ? <Button size='xs' colorPalette='blue' onClick={e => switchTeam(t._id)}>Switch</Button>
                        : <Badge colorPalette='green'>You're in this team</Badge>}
                      <Button ml={2} size='xs' as={Link} to={`/root/teams/${t._id}/edit`}>Edit</Button>
                    </Box>
                  ])}
                />
              </Box>)
          },
          {
            title: 'Users',
            content: (
              <Box>
                <SimpleTable
                  tableProps={{ size: 'sm' }}
                  headers={['Email', 'Name', 'Team', 'Last seen']}
                  rows={users.map(u => [
                    u.email,
                    `${u.firstName} ${u.lastName}`,
                    <Text key={u._id}>
                      {u.ownerTeam?.name}
                      {u.ownerTeam?._id !== user.team && <Button size='xs' ml={2} colorPalette='blue' onClick={e => switchTeam(u.ownerTeam._id)}>Switch</Button>}
                    </Text>,
                    <Text key={u._id}>{u.lastSeenAt ? utils.formatDateTime(u.lastSeenAt) : <Badge>Never</Badge>}</Text>
                  ])}
                />
              </Box>)
          },
          {
            title: 'Contacts & messages',
            content: (
              <Box>
                <Box display={{ md: 'flex' }} alignItems='top'>
                  <Box>
                    <Heading as='h3' size='md'>Contacts</Heading>
                    <Input size='sm' w='300px' mt={2} placeholder='Search contacts' value={contactQuery} onChange={e => setContactQuery(e.target.value)} />
                    <SimpleTable
                      tableProps={{ size: 'xs', w: '300px' }}
                      headers={[]}
                      rows={contacts.map(c => c
                        ? [
                          <Text key={c._id}><Text cursor='pointer' fontSize='xs' onClick={() => setSelectedContact(c)}>{c}</Text></Text>
                          ]
                        : null)}
                    />
                  </Box>

                  {selectedContact &&
                    <Box flex={1} ml={2}>
                      <Heading textAlign='center' as='h3' size='sm'>{selectedContact}</Heading>
                      <Box h='500px' overflowY='auto'>
                        <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
                          <Textarea mt={2} size='xs' placeholder='Type a message' value={newMessage} onChange={e => setNewMessage(e.target.value)} />
                          <Button ml={2} colorPalette='primary' onClick={sendMessage}>Send</Button>
                        </Box>
                        {messages?.map(m =>
                          <Box key={m._id} display='flex' justifyContent={m.inbound ? 'start' : 'end'} mt={5}>
                            <Box p={1} borderRadius='md' mb={2} bg={m.inbound ? 'bg.subtle' : 'bg.accent'} w='80%'>
                              <Text fontSize='xs' fontWeight='bold'>{utils.formatDateTime(m.createdAt)}</Text>
                              <Text fontSize='sm' whiteSpace='pre-line'>{m.body}</Text>
                            </Box>
                          </Box>)}
                      </Box>
                    </Box>}
                </Box>
              </Box>)
          },
          {
            title: 'Settings',
            content: (
              <Box>
                <Button onClick={testSentry}>Test Sentry</Button>

                <Heading mt={4} size='sm'>Run scheduled jobs</Heading>
                <p>Note that this is for testing only. Some of these jobs send comms out to users and participants.</p>
                <Box display='flex-wrap'>
                  {JOBS.map(job =>
                    <Button key={job} size='sm' mr={2} mb={2} onClick={e => runJob(job)}>{job}</Button>
                  )}
                </Box>
              </Box>)
          }
        ]}
      />

    </Box>
  )
}
